import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Input } from './input';
import { Lock, Shield, AlertTriangle, Loader2, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAccess = ({ onAccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/verify`, {
        username: username,
        password: password
      });
      
      if (response.data.success) {
        localStorage.setItem('adminAccess', 'true');
        onAccess(true);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid username or password. Please try again.');
      } else {
        setError('Unable to verify. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
      {/* Restricted Area Notice */}
      <div className="absolute top-0 left-0 right-0 bg-red-600 text-white py-3 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-3">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-semibold">RESTRICTED AREA</span>
          <span className="text-red-200">|</span>
          <span className="text-sm">Authorised Personnel Only - Unauthorised access is prohibited</span>
        </div>
      </div>

      <Card className="w-full max-w-md mt-12 border-2 border-red-200">
        <CardHeader className="text-center bg-gray-50 rounded-t-lg">
          <div className="mx-auto bg-red-600 rounded-full p-3 w-16 h-16 flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-dark">
            Admin Dashboard Access
          </CardTitle>
          <p className="text-subtle">
            Enter your credentials to view franchise leads
          </p>
        </CardHeader>
        
        <CardContent className="pt-6">
          {/* Security Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800">Security Notice</p>
                <p className="text-amber-700">This area is restricted to authorised administrators only. All access attempts are logged and monitored.</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dark mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Username / Email
              </label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError('');
                }}
                className="w-full"
                required
                data-testid="admin-username-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-dark mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                Password
              </label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                className="w-full"
                required
                data-testid="admin-password-input"
              />
              {error && (
                <p className="text-red-600 text-sm mt-2">{error}</p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-accent-primary hover:bg-accent-primary/90"
              style={{ color: 'white' }}
              disabled={isLoading}
              data-testid="admin-login-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-xs text-subtle-light">
              🔒 Secured admin access for franchise lead management
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};